import { type SelectedEntity, useSelectedEntity } from "@fero/hooks";
import { AppRoutes } from "@fero/utils";
import RightArrowIcon from "@mui/icons-material/ArrowRightAlt";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";
import HouseOutlinedIcon from "@mui/icons-material/HouseOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import StockRevaluationIcon from "@mui/icons-material/PriceChangeOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Box,
  Collapse,
  Drawer,
  type DrawerProps,
  FormControl,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import ListSubheader from "@mui/material/ListSubheader";
import type { WithDbId } from "adl-gen/common/db";
import type { UserMembership } from "adl-gen/ferovinum/app/api";
import {
  type UiConfigValues,
  useUiConfig,
} from "adl-service/hooks/use-ui-config";
import { LinkBehavior } from "components/widgets/link/link";
import _, { sortBy } from "lodash";
import React, { type FC, useCallback, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useLayoutInfo } from "../layout-info";
import type { EntityKind, IdToStore } from "../portal-page-layout/portal-page";

export interface NavigationDrawerProps
  extends Omit<DrawerHeaderProps, "selectedEntity"> {
  membership: UserMembership;
  open: boolean;
  close?(): void;
}

export const NAVIGATION_DRAWER_WIDTH = 220;
export const NavigationDrawer: FC<NavigationDrawerProps> = ({
  membership,
  onEntityChange,
  open,
  close,
}) => {
  const selectedEntity = useSelectedEntity();
  const { isMobile } = useLayoutInfo();
  const variant: DrawerProps["variant"] = isMobile ? "temporary" : "persistent";
  const width = isMobile ? "100%" : NAVIGATION_DRAWER_WIDTH;
  const onItemClicked = isMobile ? close : undefined;

  return (
    <Drawer
      variant={variant}
      anchor="left"
      open={open}
      sx={{
        width: open ? width : 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width,
          boxSizing: "border-box",
          position: "relative",
        },
        position: "relative",
      }}
    >
      <DrawerHeader
        membership={membership}
        selectedEntity={selectedEntity}
        onEntityChange={onEntityChange}
      />
      <NavMenuItems
        selectedEntity={selectedEntity}
        onItemClicked={onItemClicked}
      />
    </Drawer>
  );
};

function encodeEntity(type: EntityKind, id: string) {
  return `${type}:${id}`;
}

interface SelectedId {
  type: EntityKind;
  id: string;
}

function decodeSelectedEntity(encoded: string): SelectedId | undefined {
  const parts = _.split(encoded, ":");
  if (parts.length === 2) {
    const type = parts[0];
    if (
      type === "org" ||
      type === "storageLoc" ||
      type === "carrier" ||
      type === "supplier" ||
      type === "purchaser"
    ) {
      return { type, id: parts[1] };
    }
  }
  return undefined;
}

interface DrawerHeaderProps {
  membership: UserMembership;
  selectedEntity: SelectedEntity;
  onEntityChange(entityUpdate: IdToStore): void;
}

const DrawerHeader: FC<DrawerHeaderProps> = ({
  membership,
  selectedEntity,
  onEntityChange,
}) => {
  const [searchText, setSearchText] = useState("");
  const selectedEntityOption = selectedEntity.type
    ? encodeEntity(selectedEntity.type, selectedEntity.value.id)
    : "";
  const isMobile = useLayoutInfo().isMobile;

  const displaySubheaders = useMemo(() => {
    return (
      [
        membership.organisations,
        membership.storageLocations,
        membership.carriers,
        membership.suppliers,
      ].filter((m) => m.length > 0).length > 1
    );
  }, [membership]);

  const buildMenuItems = useCallback(
    function menuItems<T>(
      membershipList: WithDbId<T>[],
      type: EntityKind,
      getName: (val: T) => string,
      subHeaderName: string,
    ): React.ReactNode[] {
      if (membershipList.length === 0) return [];

      const components: React.ReactNode[] = [];

      const filteredSortedMembership = sortBy(
        membershipList.filter((val) =>
          getName(val.value).toLowerCase().includes(searchText.toLowerCase()),
        ),
        (val) => getName(val.value).toLowerCase(),
      );

      if (displaySubheaders) {
        components.push(
          <ListSubheader
            key={`${type}-subheader`}
            sx={{ color: "black", paddingY: 1 }}
          >
            <Typography variant="h5">{subHeaderName}</Typography>
          </ListSubheader>,
        );
      }

      filteredSortedMembership.forEach((val, i) =>
        components.push(
          <MenuItem value={encodeEntity(type, val.id)} key={`${type}-${i}`}>
            {getName(val.value)}
          </MenuItem>,
        ),
      );

      return components;
    },
    [displaySubheaders, searchText],
  );

  const handleSelectOptionChange = useCallback(
    (ev: SelectChangeEvent<string>) => {
      const entity = decodeSelectedEntity(ev.target.value);
      if (entity) {
        onEntityChange({ kind: entity.type, value: entity.id });
        setSearchText("");
      }
    },
    [onEntityChange],
  );

  const membershipDetails = useMemo<{
    singleMembershipName: string | undefined;
    count: number;
  }>(() => {
    const { organisations, storageLocations, carriers, suppliers, purchasers } =
      membership;
    const membershipCount =
      organisations.length +
      storageLocations.length +
      carriers.length +
      suppliers.length +
      purchasers.length;

    if (membershipCount !== 1) {
      return { singleMembershipName: undefined, count: membershipCount };
    }

    const singleMembershipName =
      organisations.length > 0
        ? organisations[0].value.businessName
        : storageLocations.length > 0
          ? storageLocations[0].value.locationName
          : carriers.length > 0
            ? carriers[0].value.name
            : suppliers.length > 0
              ? suppliers[0].value.name
              : purchasers.length > 0
                ? purchasers[0].value.name
                : undefined;

    return { singleMembershipName, count: membershipCount };
  }, [membership]);

  return (
    <>
      {membershipDetails.singleMembershipName ? (
        <Box
          sx={{
            padding: "20px 16px",
            borderBottom: (theme) => `1px solid ${theme.palette.common.grey3}`,
          }}
        >
          {membershipDetails.singleMembershipName}
        </Box>
      ) : (
        <FormControl fullWidth variant="standard">
          <Select
            value={selectedEntityOption}
            onClose={() => {
              setSearchText("");
            }}
            onChange={handleSelectOptionChange}
            slotProps={{
              input: {
                sx: {
                  padding: "20px 16px",
                },
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  left: "0 !important",
                  minWidth: isMobile ? "100%" : "600px !important",
                  height: "100%",
                },
              },
              autoFocus: false,
            }}
          >
            {membershipDetails.count > 10 && (
              <ListSubheader
                sx={{
                  position: "sticky",
                  top: "12px",
                  backgroundColor: "white",
                  zIndex: 1000,
                }}
              >
                <TextField
                  size="small"
                  placeholder="Type to search..."
                  fullWidth
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Escape") {
                      e.stopPropagation();
                    }
                  }}
                />
              </ListSubheader>
            )}
            {buildMenuItems(
              membership.organisations,
              "org",
              (o) => o.businessName,
              "Organisations",
            )}
            {buildMenuItems(
              membership.storageLocations,
              "storageLoc",
              (o) => o.locationName,
              "Storage locations",
            )}
            {buildMenuItems(
              membership.carriers,
              "carrier",
              (o) => o.name,
              "Carriers",
            )}
            {buildMenuItems(
              membership.suppliers,
              "supplier",
              (o) => o.name,
              "Suppliers",
            )}
            {buildMenuItems(
              membership.purchasers,
              "purchaser",
              (o) => o.name,
              "Purchasers",
            )}
          </Select>
        </FormControl>
      )}
    </>
  );
};

interface NavMenuItem {
  type: "single";
  title: string;
  route: AppRoutes;
  isActive: boolean;
  Icon: typeof SvgIcon;
}

interface NavMenuGroup {
  type: "group";
  title: string;
  isActive: boolean;
  initialRoute: AppRoutes;
  Icon: typeof SvgIcon;
  submenus: SubMenuItem[];
}

interface SubMenuItem {
  title: string;
  route: AppRoutes;
}

const getOrganisationMenuItems = (
  currentPath: string,
  uiConfig: UiConfigValues,
): (NavMenuItem | NavMenuGroup)[] => {
  const {
    showProcurement,
    showNominatedPurchaser,
    showProductionOrder,
    showStockMovement,
    showStockRevaluation,
    showStorefront,
    showNewTradeSaleUI,
  } = uiConfig;
  const menuItems: (NavMenuItem | NavMenuGroup)[] = [];
  menuItems.push({
    type: "single",
    title: "Dashboard",
    route: AppRoutes.Dashboard,
    isActive: currentPath === AppRoutes.Dashboard,
    Icon: HouseOutlinedIcon,
  });
  menuItems.push({
    type: "single",
    title: "Inventory",
    route: AppRoutes.Inventory,
    isActive: currentPath === AppRoutes.Inventory,
    Icon: Inventory2OutlinedIcon,
  });
  menuItems.push({
    type: "single",
    title: "Sale Orders",
    route: AppRoutes.RepurchaseSaleOrders,
    isActive: currentPath === AppRoutes.RepurchaseSaleOrders,
    Icon: ReceiptLongOutlinedIcon,
  });
  if (showProcurement) {
    menuItems.push({
      type: "single",
      title: "Upload Stock",
      route: AppRoutes.OrganisationNewDealRequests,
      isActive: currentPath.startsWith(AppRoutes.OrganisationNewDealRequests),
      Icon: LocalOfferOutlinedIcon,
    });
  }
  if (showProductionOrder) {
    menuItems.push({
      type: "single",
      title: "Production Orders",
      route: AppRoutes.OrgProductionOrders,
      isActive: currentPath === AppRoutes.OrgProductionOrders,
      Icon: HandymanOutlinedIcon,
    });
  }
  if (showStockMovement) {
    menuItems.push({
      type: "single",
      title: "Stock Movement",
      route: AppRoutes.StockMovement,
      isActive: currentPath.startsWith(AppRoutes.StockMovement),
      Icon: LocalShippingOutlinedIcon,
    });
  }
  if (showStockRevaluation) {
    menuItems.push({
      type: "single",
      title: "Stock Revaluation",
      route: AppRoutes.StockRevaluation,
      isActive: currentPath === AppRoutes.StockRevaluation,
      Icon: StockRevaluationIcon,
    });
  }
  if (showNominatedPurchaser) {
    menuItems.push({
      type: "group",
      title: "Trade Sales",
      Icon: StorefrontIcon,
      initialRoute: AppRoutes.OrganisationPurchaseRequests,
      isActive:
        currentPath === AppRoutes.OrganisationPurchaseRequests ||
        currentPath === AppRoutes.OrganisationPurchaseRequestByPurchaser ||
        currentPath === AppRoutes.Storefront ||
        currentPath === AppRoutes.OrganisationPurchaseRequestPurchasers ||
        currentPath === AppRoutes.OrganisationPurchaseRequestProducts ||
        currentPath === AppRoutes.NewPurchaserAllocation,
      submenus: [
        {
          title: "By Purchaser",
          route: AppRoutes.OrganisationPurchaseRequestByPurchaser,
        },
        ...(showNewTradeSaleUI
          ? [
              {
                title: "Purchasers",
                route: AppRoutes.OrganisationPurchaseRequestPurchasers,
              },
              {
                title: "Products",
                route: AppRoutes.OrganisationPurchaseRequestProducts,
              },
            ]
          : []),
        ...(showStorefront
          ? [
              {
                title: "Storefront",
                route: AppRoutes.Storefront,
              },
            ]
          : []),
      ],
    });
  }
  menuItems.push({
    type: "single",
    title: "Expiring Stock",
    route: AppRoutes.OrganisationExpiringStock,
    isActive: currentPath === AppRoutes.OrganisationExpiringStock,
    Icon: WarningAmberOutlinedIcon,
  });
  return menuItems;
};

const getStorageLocationMenuItems = (
  currentPath: string,
  showProcurement: boolean,
): NavMenuItem[] => {
  const menuItems: NavMenuItem[] = [
    {
      type: "single",
      title: "Production Orders",
      route: AppRoutes.LocProductionOrders,
      isActive:
        currentPath === AppRoutes.LocProductionOrders ||
        currentPath === AppRoutes.LocExistingProductionOrders,
      Icon: HandymanOutlinedIcon,
    },
    {
      type: "single",
      title: "Product Balances",
      route: AppRoutes.ProductBalances,
      isActive: currentPath === AppRoutes.ProductBalances,
      Icon: Inventory2OutlinedIcon,
    },
  ];

  if (showProcurement) {
    menuItems.push({
      type: "single",
      title: "Shipments",
      route: AppRoutes.LocNewDealRequestsIncoming,
      isActive: currentPath === AppRoutes.LocNewDealRequestsIncoming,
      Icon: LocalShippingRight,
    });
  }

  menuItems.push({
    type: "single",
    title: "Outgoing orders",
    route: AppRoutes.StorageLocationOutgoingOrders,
    isActive: currentPath.startsWith(AppRoutes.StorageLocationOutgoingOrders),
    // @ts-ignore - Could not figure out the typescript acrobatics to make this work
    Icon: LocalShippingLeft,
  });

  return menuItems;
};

const LocalShippingRight = LocalShippingOutlinedIcon;
const LocalShippingLeft = () => (
  <LocalShippingOutlinedIcon
    sx={{
      transform: "matrix(-1, 0, 0, 1, 0, 0)",
    }}
  />
);

const getCarrierMenuItems = (currentPath: string): NavMenuItem[] => {
  return [
    {
      type: "single",
      title: "Collection Orders",
      route: AppRoutes.CarrierRequests,
      isActive: currentPath.startsWith(AppRoutes.CarrierRequests),
      Icon: LocalShippingOutlinedIcon,
    },
  ];
};

const getSupplierMenuItems = (currentPath: string): NavMenuItem[] => {
  return [
    {
      type: "single",
      title: "Purchase Orders",
      route: AppRoutes.SupplierNewDealRequests,
      isActive: currentPath.startsWith(AppRoutes.SupplierNewDealRequests),
      Icon: ReceiptLongOutlinedIcon,
    },
  ];
};

const getPurchaserMenuItems = (currentPath: string): NavMenuItem[] => {
  return [
    {
      type: "single",
      title: "Orders",
      route: AppRoutes.PurchaserPurchaseRequests,
      isActive: currentPath === AppRoutes.PurchaserPurchaseRequests,
      Icon: StorefrontIcon,
    },
  ];
};

interface NavMenuItemsProps {
  selectedEntity: SelectedEntity;
  onItemClicked?: () => void;
}

const NavMenuItems: FC<NavMenuItemsProps> = ({
  selectedEntity,
  onItemClicked,
}) => {
  const uiConfig = useUiConfig();
  const location = useLocation();
  const history = useHistory();

  const navMenuItems = useMemo(() => {
    if (selectedEntity.type === "org") {
      return getOrganisationMenuItems(location.pathname, uiConfig);
    }
    if (selectedEntity.type === "storageLoc") {
      return getStorageLocationMenuItems(
        location.pathname,
        uiConfig.showProcurement,
      );
    }
    if (selectedEntity.type === "carrier") {
      return getCarrierMenuItems(location.pathname);
    }
    if (selectedEntity.type === "supplier") {
      return getSupplierMenuItems(location.pathname);
    }
    if (selectedEntity.type === "purchaser") {
      return getPurchaserMenuItems(location.pathname);
    }
    return [];
  }, [selectedEntity.type, location.pathname, uiConfig]);

  return (
    <List>
      {navMenuItems.map((value) => {
        if (value.type === "single") {
          const { title, Icon, isActive, route } = value;
          return (
            <ListItem key={title} disablePadding>
              <ListItemButton
                onClick={onItemClicked}
                // LinkComponent used underneath needs to be defined here instead of app-theme
                // See: https://github.com/mui/material-ui/issues/29030
                component={LinkBehavior}
                selected={location.pathname === route}
                href={route}
                sx={{
                  "&.Mui-selected": { backgroundColor: "background.default" },
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: "unset", pr: 2, color: "common.darkGrey" }}
                >
                  <Icon />
                </ListItemIcon>
                <ListItemText
                  primary={title}
                  primaryTypographyProps={{ fontWeight: isActive ? 700 : 400 }}
                />
              </ListItemButton>
            </ListItem>
          );
        }
        if (value.type === "group") {
          const { title, Icon, isActive, initialRoute, submenus } = value;
          return (
            <React.Fragment key={title}>
              <ListItem key={title} disablePadding>
                <ListItemButton
                  onClick={() => {
                    history.push(initialRoute);
                    if (onItemClicked) onItemClicked();
                  }}
                  component={LinkBehavior}
                  selected={isActive}
                  href={initialRoute}
                  sx={{
                    "&.Mui-selected": { backgroundColor: "background.default" },
                  }}
                >
                  <ListItemIcon
                    sx={{ minWidth: "unset", pr: 2, color: "common.darkGrey" }}
                  >
                    <Icon />
                  </ListItemIcon>
                  <ListItemText
                    primary={title}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 700 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
              {isActive && (
                <Collapse in={true} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {submenus.map((smenu, idx) => {
                      const submenuSelected = location.pathname === smenu.route;
                      return (
                        <ListItemButton
                          key={idx}
                          sx={{ pl: 4 }}
                          href={smenu.route}
                          selected={submenuSelected}
                          onClick={() => {
                            history.push(smenu.route);
                          }}
                        >
                          <ListItemIcon>
                            {submenuSelected && <RightArrowIcon />}
                          </ListItemIcon>
                          <ListItemText primary={smenu.title} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        }
        return null;
      })}
    </List>
  );
};
